// src/pages/superadmin/SuperAdminStaff.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, Plus, Edit2, Trash2, Search, X, CheckCircle, XCircle,
  Mail, Phone, User, Shield, RefreshCw, Key, MapPin, Building,
  MessageCircle, Save, Calendar
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuperAdminStaff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'sysadmin',
    phone: '',
    phone_mobile: '',
    phone_office: '',
    address: '',
    city: '',
    state: 'TX',
    zip_code: '',
    telegram_username: '',
    telegram_notifications: false,
    is_active: true,
    password: ''
  });

  useEffect(() => {
    loadStaff();
  }, [searchTerm]);

  const loadStaff = async () => {
    setLoading(true);
    try {
      // ✅ CORRIGÉ : Utilise /admin/platform/staff
      const response = await axios.get(`${API_BASE}/admin/platform/staff`, {
        params: { search: searchTerm, limit: 100 }
      });
      setStaff(response.data || []);
    } catch (error) {
      console.error('Error loading platform staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.first_name || !formData.last_name) {
      alert('Email, first name and last name are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingStaff) {
        // ✅ CORRIGÉ : PUT vers /admin/platform/staff/{id}
        await axios.put(`/admin/platform/staff/${editingStaff.id}`, {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          phone: formData.phone,
          phone_mobile: formData.phone_mobile,
          phone_office: formData.phone_office,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          telegram_username: formData.telegram_username,
          telegram_notifications: formData.telegram_notifications,
          is_active: formData.is_active
        });
      } else {
        if (!formData.password) {
          alert('Password is required for new staff');
          setSubmitting(false);
          return;
        }
        // ✅ CORRIGÉ : POST vers /admin/platform/staff
        await axios.post('/admin/platform/staff', {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
          phone: formData.phone,
          phone_mobile: formData.phone_mobile,
          phone_office: formData.phone_office,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          telegram_username: formData.telegram_username,
          telegram_notifications: formData.telegram_notifications,
          password: formData.password,
          is_active: formData.is_active
        });
      }
      setShowModal(false);
      resetForm();
      await loadStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      alert(error.response?.data?.detail || 'Error saving staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (staffMember) => {
    if (window.confirm(`Delete staff "${staffMember.first_name} ${staffMember.last_name}"?`)) {
      try {
        // ✅ CORRIGÉ : DELETE vers /admin/platform/staff/{id}
        await axios.delete(`/admin/platform/staff/${staffMember.id}`);
        await loadStaff();
      } catch (error) {
        console.error('Error deleting staff:', error);
        alert('Error deleting staff');
      }
    }
  };

  const handleResetPassword = async (staffMember) => {
    setSelectedStaff(staffMember);
    setNewPassword('');
    setShowResetModal(true);
  };

  const confirmResetPassword = async () => {
    if (!newPassword) {
      alert('Please enter a new password');
      return;
    }
    try {
      // ✅ CORRIGÉ : POST vers /admin/platform/staff/{id}/reset-password
      await axios.post(`/admin/platform/staff/${selectedStaff.id}/reset-password`, {
        new_password: newPassword
      });
      setResetResult({
        staff: `${selectedStaff.first_name} ${selectedStaff.last_name}`,
        password: newPassword
      });
      setShowResetModal(false);
      setTimeout(() => setResetResult(null), 10000);
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      first_name: '',
      last_name: '',
      role: 'sysadmin',
      phone: '',
      phone_mobile: '',
      phone_office: '',
      address: '',
      city: '',
      state: 'TX',
      zip_code: '',
      telegram_username: '',
      telegram_notifications: false,
      is_active: true,
      password: ''
    });
    setEditingStaff(null);
    setActiveTab('general');
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      email: staffMember.email || '',
      first_name: staffMember.first_name || '',
      last_name: staffMember.last_name || '',
      role: staffMember.role || 'sysadmin',
      phone: staffMember.phone || '',
      phone_mobile: staffMember.phone_mobile || '',
      phone_office: staffMember.phone_office || '',
      address: staffMember.address || '',
      city: staffMember.city || '',
      state: staffMember.state || 'TX',
      zip_code: staffMember.zip_code || '',
      telegram_username: staffMember.telegram_username || '',
      telegram_notifications: staffMember.telegram_notifications || false,
      is_active: staffMember.is_active !== false,
      password: ''
    });
    setShowModal(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { label: 'Super Admin', color: 'bg-red-500/20 text-red-400', icon: Shield },
      sysadmin: { label: 'SysAdmin', color: 'bg-purple-500/20 text-purple-400', icon: Shield },
      admin: { label: 'Admin', color: 'bg-blue-500/20 text-blue-400', icon: User },
      manager: { label: 'Manager', color: 'bg-green-500/20 text-green-400', icon: User },
      staff: { label: 'Staff', color: 'bg-gray-500/20 text-gray-400', icon: User }
    };
    const c = config[role] || config.staff;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${c.color}`}>
        <Icon className="w-3 h-3" /> {c.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Platform Staff</h2>
          <p className="text-gray-400 text-sm">Manage your internal team (Super Admin & SysAdmin)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Staff
        </button>
      </div>

      {/* Reset Password Result */}
      {resetResult && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 font-medium">Password reset for {resetResult.staff}</p>
          <p className="text-white text-lg font-mono mt-1">{resetResult.password}</p>
          <p className="text-yellow-400 text-xs mt-1">⚠️ Give this password to the staff member. It won't be shown again!</p>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white"
        />
      </div>

      {/* Staff Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr className="text-gray-400 text-sm">
                <th className="p-4 text-left">Staff</th>
                <th className="p-4 text-left">Email</th>
                <th className="p-4 text-left">Role</th>
                <th className="p-4 text-left">Phone</th>
                <th className="p-4 text-left">Telegram</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <tr key={member.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{member.first_name} {member.last_name}</p>
                      <p className="text-gray-500 text-xs">ID: {member.id.substring(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-gray-500" />
                      <span className="text-gray-300">{member.email}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {getRoleBadge(member.role)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-gray-500" />
                      <span className="text-gray-300">{member.phone || '-'}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {member.telegram_username ? (
                      <div className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3 text-sky-400" />
                        <span className="text-sky-400 text-xs">@{member.telegram_username}</span>
                      </div>
                    ) : (
                      <span className="text-gray-500 text-xs">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    {member.is_active ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-400 text-xs">
                        <XCircle className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleResetPassword(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400" title="Reset Password">
                        <Key className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(member)} className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">
                    No platform staff found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Create/Edit - avec onglets */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">{editingStaff ? 'Edit Staff' : 'New Staff'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-700 px-4">
              {['general', 'contact', 'address', 'telegram'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-3 py-2 text-sm capitalize transition ${activeTab === tab ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-white'}`}
                >
                  {tab === 'general' && <User className="w-3 h-3 inline mr-1" />}
                  {tab === 'contact' && <Phone className="w-3 h-3 inline mr-1" />}
                  {tab === 'address' && <MapPin className="w-3 h-3 inline mr-1" />}
                  {tab === 'telegram' && <MessageCircle className="w-3 h-3 inline mr-1" />}
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* General Tab */}
              {activeTab === 'general' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">First Name *</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Last Name *</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="sysadmin">SysAdmin (Level 105) - Infrastructure</option>
                      <option value="super_admin">Super Admin (Level 110) - Full Access</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600"
                    />
                    <label className="text-sm text-gray-300">Active (can log in)</label>
                  </div>
                  {!editingStaff && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Password *</label>
                      <input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                    </div>
                  )}
                </>
              )}

              {/* Contact Tab */}
              {activeTab === 'contact' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Phone (Main)</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Phone (Mobile)</label>
                    <input
                      type="tel"
                      value={formData.phone_mobile}
                      onChange={(e) => setFormData({ ...formData, phone_mobile: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Phone (Office)</label>
                    <input
                      type="tel"
                      value={formData.phone_office}
                      onChange={(e) => setFormData({ ...formData, phone_office: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </>
              )}

              {/* Address Tab */}
              {activeTab === 'address' && (
                <>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Address</label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      placeholder="Street address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Zip Code</label>
                    <input
                      type="text"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </>
              )}

              {/* Telegram Tab */}
              {activeTab === 'telegram' && (
                <>
                  <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-sky-400" />
                      <h4 className="text-white font-medium text-sm">Telegram Notifications</h4>
                    </div>
                    <p className="text-gray-400 text-xs">
                      Staff with Telegram can receive instant alerts.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Telegram Username</label>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">@</span>
                      <input
                        type="text"
                        value={formData.telegram_username}
                        onChange={(e) => setFormData({ ...formData, telegram_username: e.target.value.replace('@', '') })}
                        className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.telegram_notifications}
                      onChange={(e) => setFormData({ ...formData, telegram_notifications: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-600"
                    />
                    <label className="text-sm text-gray-300">Enable Telegram notifications</label>
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {submitting ? 'Saving...' : (editingStaff ? 'Update' : 'Create')}
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Reset Password */}
      {showResetModal && selectedStaff && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Reset Password</h3>
              <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-gray-300 text-sm">
                Reset password for <span className="text-white font-medium">{selectedStaff.first_name} {selectedStaff.last_name}</span>
              </p>
              <div>
                <label className="block text-sm text-gray-400 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Enter new password"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={confirmResetPassword}
                className="flex-1 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white font-medium"
              >
                Reset Password
              </button>
              <button
                onClick={() => setShowResetModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminStaff;